import { useEffect, useRef, useState } from "react";
import axios from "axios";
import DashboardLayout from "../layout/DashboardLayout";
import "../layout/Dashboard.css";
import "./OccupancyPage.css";

const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:5050";
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

interface DayData {
  date: string;
  booked: number;
  total: number;
  occupancy: number;
}

function occColor(pct: number) {
  if (pct === 0)  return { bg: "rgba(255,255,255,0.03)",               bar: "rgba(201,168,76,0.12)",  text: "#94a3b8",  border: "rgba(201,168,76,0.12)" };
  if (pct <= 25)  return { bg: "rgba(52,211,153,0.10)", bar: "#10b981",  text: "#059669",  border: "rgba(16,185,129,0.25)" };
  if (pct <= 50)  return { bg: "rgba(251,191,36,0.12)", bar: "#f59e0b",  text: "#b45309",  border: "rgba(245,158,11,0.30)" };
  if (pct <= 75)  return { bg: "rgba(249,115,22,0.12)", bar: "#f97316",  text: "#c2410c",  border: "rgba(249,115,22,0.30)" };
  return              { bg: "rgba(239,68,68,0.14)",  bar: "#ef4444",  text: "#b91c1c",  border: "rgba(239,68,68,0.35)" };
}

function groupByMonth(days: DayData[]) {
  const map: Record<string, DayData[]> = {};
  for (const d of days) {
    const dt = new Date(d.date + "T12:00:00");
    const key = `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,"0")}`;
    if (!map[key]) map[key] = [];
    map[key].push(d);
  }
  return map;
}

function monthLabel(key: string) {
  const [y, m] = key.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

function firstWeekday(key: string) {
  const [y, m] = key.split("-");
  return new Date(Number(y), Number(m) - 1, 1).getDay();
}

export default function OccupancyPage() {
  const [days, setDays]       = useState<DayData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const [tooltip, setTooltip] = useState<{ day: DayData; x: number; y: number } | null>(null);
  const todayISO = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    axios.get(`${API_BASE}/api/dashboard/occupancy-heatmap`)
      .then(res => {
        const raw = (Array.isArray(res.data) ? res.data : res.data.days || [])
          .map((d: any) => ({ ...d, occupancy: d.pct ?? d.occupancy ?? 0 }));
        setDays(raw);
      })
      .catch(() => setError("Failed to load occupancy data"))
      .finally(() => setLoading(false));
  }, []);

  const byMonth = groupByMonth(days);

  const avgOcc = days.length
    ? Math.round(days.reduce((s, d) => s + d.occupancy, 0) / days.length)
    : 0;
  const peakDay = days.reduce<DayData | null>((b, d) => (!b || d.occupancy > b.occupancy ? d : b), null);
  const lowDay  = days.filter(d => d.occupancy > 0)
                      .reduce<DayData | null>((b, d) => (!b || d.occupancy < b.occupancy ? d : b), null);
  const totalBooked = days.filter(d => d.booked > 0).length;

  const fmt = (iso: string) =>
    new Date(iso + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });

  return (
    <DashboardLayout>
      <div className="occ-page">

        {/* ── Header ── */}
        <div className="occ-header">
          <div>
            <h1 className="occ-title">Occupancy Heatmap</h1>
            <p className="occ-subtitle">Past 90 days · Today · Next 90 days</p>
          </div>
        </div>

        {loading && <div className="occ-loading"><span className="occ-spinner" /> Loading…</div>}
        {error   && <div className="occ-error">{error}</div>}

        {!loading && !error && (
          <>
            {/* ── KPI Strip ── */}
            <div className="occ-kpi-row">
              <div className="occ-kpi-card">
                <span className="occ-kpi-label">Average Occupancy</span>
                <span className="occ-kpi-value">{avgOcc}<span className="occ-kpi-unit">%</span></span>
                <div className="occ-kpi-bar-track">
                  <div className="occ-kpi-bar-fill" style={{ width: `${avgOcc}%`, background: occColor(avgOcc).bar }} />
                </div>
              </div>
              <div className="occ-kpi-card">
                <span className="occ-kpi-label">Peak Day</span>
                <span className="occ-kpi-value" style={{ color: "#ef4444" }}>
                  {peakDay ? peakDay.occupancy : "—"}{peakDay ? <span className="occ-kpi-unit">%</span> : ""}
                </span>
                {peakDay && <span className="occ-kpi-sub">{fmt(peakDay.date)}</span>}
              </div>
              <div className="occ-kpi-card">
                <span className="occ-kpi-label">Lowest Occupied Day</span>
                <span className="occ-kpi-value" style={{ color: "#34d399" }}>
                  {lowDay ? lowDay.occupancy : "—"}{lowDay ? <span className="occ-kpi-unit">%</span> : ""}
                </span>
                {lowDay && <span className="occ-kpi-sub">{fmt(lowDay.date)}</span>}
              </div>
              <div className="occ-kpi-card">
                <span className="occ-kpi-label">Days with Bookings</span>
                <span className="occ-kpi-value">{totalBooked}<span className="occ-kpi-unit"> days</span></span>
                <span className="occ-kpi-sub">out of {days.length} tracked</span>
              </div>
            </div>

            {/* ── Calendar ── */}
            <div className="occ-calendar-card">
              {days.length === 0 ? (
                <div className="occ-empty">No occupancy data available</div>
              ) : (
                Object.entries(byMonth).map(([key, monthDays]) => {
                  const offset = firstWeekday(key);
                  return (
                    <div key={key} className="occ-month-block">
                      <div className="occ-month-header">
                        <span className="occ-month-name">{monthLabel(key)}</span>
                        <span className="occ-month-avg">
                          avg {Math.round(monthDays.reduce((s,d) => s+d.occupancy,0)/monthDays.length)}%
                        </span>
                      </div>

                      {/* Weekday headers */}
                      <div className="occ-weekday-row">
                        {WEEKDAYS.map(w => <div key={w} className="occ-weekday-hdr">{w}</div>)}
                      </div>

                      {/* Day grid — 7 cols with offset */}
                      <div className="occ-day-grid">
                        {Array.from({ length: offset }).map((_, i) => (
                          <div key={`pad-${i}`} className="occ-day-pad" />
                        ))}
                        {monthDays.map(d => {
                          const pct      = d.occupancy ?? 0;
                          const c        = occColor(pct);
                          const isToday  = d.date === todayISO;
                          const dayNum   = new Date(d.date + "T12:00:00").getDate();
                          return (
                            <div
                              key={d.date}
                              className={`occ-day-cell${isToday ? " occ-today" : ""}`}
                              style={{ background: c.bg, borderColor: isToday ? "rgba(14,34,64,0.10)" : c.border }}
                              onMouseEnter={e => {
                                const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                setTooltip({ day: d, x: r.left + r.width / 2, y: r.top });
                              }}
                              onMouseLeave={() => setTooltip(null)}
                            >
                              <span className={`occ-day-num${isToday ? " occ-today-num" : ""}`}>{dayNum}</span>
                              <span className="occ-day-pct" style={{ color: pct === 0 ? "#cbd5e1" : c.text }}>
                                {pct}%
                              </span>
                              {/* fill bar at bottom */}
                              <div className="occ-cell-bar">
                                <div
                                  className="occ-cell-bar-fill"
                                  style={{ width: `${pct}%`, background: c.bar }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* ── Legend ── */}
            <div className="occ-legend">
              <span className="occ-legend-title">Occupancy</span>
              {[
                { label: "0%",      bg: "rgba(201,168,76,0.07)", color: "rgba(242,235,217,0.35)" },
                { label: "1–25%",   bg: "rgba(52,211,153,0.12)",  color: "#34d399" },
                { label: "26–50%",  bg: "rgba(251,191,36,0.12)",  color: "#fbbf24" },
                { label: "51–75%",  bg: "rgba(249,115,22,0.12)",  color: "#f97316" },
                { label: "76–100%", bg: "rgba(239,68,68,0.14)",   color: "#ef4444" },
              ].map(l => (
                <div key={l.label} className="occ-legend-item" style={{ background: l.bg, borderColor: l.color + "33" }}>
                  <span style={{ color: l.color }}>{l.label}</span>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ── Tooltip ── */}
        {tooltip && (
          <div className="occ-tooltip" style={{ top: tooltip.y - 100, left: tooltip.x - 90 }}>
            <p className="occ-tt-date">
              {new Date(tooltip.day.date + "T12:00:00").toLocaleDateString("en-US", {
                weekday: "short", month: "short", day: "numeric", year: "numeric"
              })}
            </p>
            <p className="occ-tt-rooms">{tooltip.day.booked} / {tooltip.day.total} rooms booked</p>
            <p className="occ-tt-pct" style={{ color: occColor(tooltip.day.occupancy).text }}>
              {tooltip.day.occupancy}% occupancy
            </p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
